import React from "react";
import { ChevronLeft, ChevronRight, Home, Menu, FileText } from "lucide-react";

const CourseNavigation = ({
  course,
  activeModule,
  activePage,
  setActiveModule,
  setActivePage,
  setShowOverview,
  setSidebarCollapsed,
  setShowModulePages,
  // моб. меню
  isMobile,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isPagesMenuOpen,
  setIsPagesMenuOpen,
}) => {
  // ⛔️ Ранний выход — НЕТ хуков ниже!
  if (!course || !activeModule || !activePage) return null;

  // Данные без useMemo — производительно ок для Object.keys()
  const courseName = course?.name || "Курс";
  const modulesObj = course?.modules || {};
  const moduleKeys = Object.keys(modulesObj);

  const currentModuleIndex = moduleKeys.indexOf(activeModule);
  const currentModule = modulesObj[activeModule] || { title: "", pages: {} };

  const pagesObj = currentModule.pages || {};
  const pageKeys = Object.keys(pagesObj);
  const currentPageIndex = pageKeys.indexOf(activePage);

  const moduleTitle = currentModule.title || "";
  const pageTitle = pagesObj?.[activePage]?.title || "";

  const goToPage = (moduleId, pageId) => {
    setActiveModule(moduleId);
    setActivePage(pageId);
    setShowOverview(false);
    setSidebarCollapsed(true);
    setShowModulePages(false);
  };

  const handlePrev = () => {
    if (currentPageIndex > 0) {
      goToPage(activeModule, pageKeys[currentPageIndex - 1]);
    } else if (currentModuleIndex > 0) {
      const prevModuleId = moduleKeys[currentModuleIndex - 1];
      const prevPages = Object.keys(modulesObj?.[prevModuleId]?.pages || {});
      if (prevPages.length) goToPage(prevModuleId, prevPages[prevPages.length - 1]);
    }
  };

  const handleNext = () => {
    if (currentPageIndex < pageKeys.length - 1) {
      goToPage(activeModule, pageKeys[currentPageIndex + 1]);
    } else if (currentModuleIndex < moduleKeys.length - 1) {
      const nextModuleId = moduleKeys[currentModuleIndex + 1];
      const nextPages = Object.keys(modulesObj?.[nextModuleId]?.pages || {});
      if (nextPages.length) goToPage(nextModuleId, nextPages[0]);
    }
  };

  const disablePrev = currentPageIndex === 0 && currentModuleIndex === 0;
  const disableNext =
    currentPageIndex === pageKeys.length - 1 &&
    currentModuleIndex === moduleKeys.length - 1;

  return (
    <div className="course-nav relative">
      {/* Мобильные плавающие кнопки меню */}
      {isMobile && (
        <>
          <button
            onClick={() => setIsMobileMenuOpen?.(true)}
            aria-label="Открыть меню курса"
            style={floatingBtnStyle("left")}
          >
            <Menu className="w-6 h-6" />
          </button>
          <button
            onClick={() => setIsPagesMenuOpen?.(true)}
            aria-label="Открыть список страниц"
            style={floatingBtnStyle("right")}
          >
            <FileText className="w-6 h-6" />
          </button>
        </>
      )}

      <div className="flex flex-col gap-2 lg:gap-3">
        {/* Хлебные крошки */}
        <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 min-w-0">
          <button
            className="flex items-center gap-1 text-green-600 hover:text-green-700 transition-colors flex-shrink-0"
            onClick={() => {
              setShowOverview(true);
              setShowModulePages(false);
              setActiveModule(null);
              setActivePage(null);
              setSidebarCollapsed(false);
            }}
            title={courseName}
          >
            <Home className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline whitespace-nowrap">{courseName}</span>
          </button>

          <Sep />

          <button
            className="text-green-600 hover:text-green-700 transition-colors min-w-0"
            onClick={() => {
              setActivePage(null);
              setShowOverview(false);
              setShowModulePages(true);
              setSidebarCollapsed(false);
            }}
            title={moduleTitle}
          >
            <span
              className="block max-w-[40vw] sm:max-w-[50vw] lg:max-w-[30vw] whitespace-nowrap overflow-hidden text-ellipsis"
              title={moduleTitle}
            >
              {moduleTitle}
            </span>
          </button>

          <Sep />

          <span className="text-gray-600 flex-shrink-0">
            {Math.max(1, currentPageIndex + 1)}/{pageKeys.length || 1}
          </span>
        </div>

        {/* Заголовок + стрелки */}
        <div className="flex items-center justify-between gap-2">
          <h3
            className="text-gray-800 font-medium leading-tight min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
            title={pageTitle}
            style={{ fontSize: "clamp(13px, 2.2vw, 16px)" }}
          >
            {pageTitle}
          </h3>

          <div className="flex items-center gap-1 flex-shrink-0">
            <NavButton
              onClick={
                currentPageIndex === 0 && currentModuleIndex > 0
                  ? () => {
                      const prevModuleId = moduleKeys[currentModuleIndex - 1];
                      const prevPages = Object.keys(modulesObj?.[prevModuleId]?.pages || {});
                      if (prevPages.length) goToPage(prevModuleId, prevPages[prevPages.length - 1]);
                    }
                  : handlePrev
              }
              title={currentPageIndex === 0 && currentModuleIndex > 0 ? "Предыдущий модуль" : "Предыдущий урок"}
              disabled={disablePrev}
              icon={<ChevronLeft className="w-4 h-4 lg:w-5 lg:h-5" />}
            />

            <span
              className="inline-flex items-center justify-center rounded bg-gray-100 text-gray-600 text-[10px] sm:text-xs"
              style={{ width: 52, height: 28 }}
              aria-label={`Страница ${Math.max(1, currentPageIndex + 1)} из ${pageKeys.length || 1}`}
            >
              {Math.max(1, currentPageIndex + 1)}/{pageKeys.length || 1}
            </span>

            <NavButton
              onClick={
                currentPageIndex === pageKeys.length - 1 && currentModuleIndex < moduleKeys.length - 1
                  ? () => {
                      const nextModuleId = moduleKeys[currentModuleIndex + 1];
                      const nextPages = Object.keys(modulesObj?.[nextModuleId]?.pages || {});
                      if (nextPages.length) goToPage(nextModuleId, nextPages[0]);
                    }
                  : handleNext
              }
              title={
                currentPageIndex === pageKeys.length - 1 && currentModuleIndex < moduleKeys.length - 1
                  ? "Следующий модуль"
                  : "Следующий урок"
              }
              disabled={disableNext}
              icon={<ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" />}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        .course-nav {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 8px 12px;
          margin-bottom: 16px;
        }
        @media (min-width: 1024px) {
          .course-nav {
            padding: 12px 16px;
          }
        }
      `}</style>
    </div>
  );
};

function NavButton({ onClick, title, disabled, icon }) {
  return (
    <button
      className="rounded transition-colors flex items-center justify-center text-green-600 hover:text-green-700 hover:bg-green-50 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{ width: 36, height: 32 }}
    >
      {icon}
    </button>
  );
}

function Sep() {
  return <span className="text-gray-400 flex-shrink-0">›</span>;
}

function floatingBtnStyle(side = "left") {
  return {
    position: "fixed",
    top: 16,
    [side]: 16,
    zIndex: 50,
    background: "white",
    padding: 8,
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    touchAction: "manipulation",
  };
}

export default CourseNavigation;
